import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../../../../../api';
import { useLogos } from '../../../../../context/LogoProvider';

// 🔹 Configuración dinámica URLs
const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
const UPLOAD_ENDPOINT = `${API_BASE}/upload/persona`;

// Helper para construir la URL completa de la imagen (S3 o Local)
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path; // Ya es S3
  return `${SERVER_URL}${path}`; // Es local antigua
};

const EMPTY = {
  nombre: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
  curp: '',
  rfc: '',
  genero: '',
  fechaNacimiento: '',
  fotoPerfil: '',
};

// Expresiones regulares típicas para México
const RFC_REGEX = /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/; // Exactamente 13 caracteres (personas físicas)
const CURP_REGEX =
  /^[A-ZÑ][AEIOUX][A-ZÑ]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM](AS|BC|BS|CC|CS|CH|CL|CM|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[0-9A-Z]\d$/;


export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const { getSystemConfig } = useLogos();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    nombre: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    curp: '',
    rfc: '',
    genero: '',
    fechaNacimiento: '',
    fotoPerfil: '',
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [previousImagePath, setPreviousImagePath] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // ---- Helpers de validación ----
  const validateField = (field, rawValue) => {
    const value =
      field === 'curp' || field === 'rfc'
        ? (rawValue || '').toUpperCase().trim()
        : (rawValue || '').trim();

    let error = '';

    switch (field) {
      case 'nombre':
        if (!value) error = 'El nombre es obligatorio.';
        break;
      case 'apellidoPaterno':
        if (!value) error = 'El apellido paterno es obligatorio.';
        break;
      case 'apellidoMaterno':
        // Apellido materno es opcional, no se valida
        break;
      case 'curp':
        // CURP es opcional, solo validamos formato si tiene valor
        if (value && !CURP_REGEX.test(value)) {
          error = 'Verifica que la CURP tenga 18 caracteres y un formato válido.';
        }
        break;
      case 'rfc':
        // RFC es opcional, solo validamos formato si tiene valor
        if (value && !RFC_REGEX.test(value)) {
          error = 'Verifica que el RFC tenga 13 caracteres y un formato válido.';
        }
        break;
      case 'genero':
        if (!value) error = 'Selecciona un género.';
        break;
      case 'fechaNacimiento':
        if (!value) error = 'La fecha de nacimiento es obligatoria.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, fotoPerfil: 'Solo se permiten archivos de imagen.' }));
      return;
    }

    const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
    if (file.size > maxMB * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, fotoPerfil: `La imagen no debe superar los ${maxMB}MB.` }));
      return;
    }

    setSelectedFile(file);
    const blobUrl = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, fotoPerfilPreview: blobUrl }));
    setErrors((prev) => ({ ...prev, fotoPerfil: '' }));
  };

  const validateAll = () => {
    const fields = Object.keys(form);
    return fields.every((f) => validateField(f, form[f]));
  };

  const handleChange = (field, value) => {
    // Normalizamos CURP y RFC a mayúsculas
    const newValue =
      field === 'curp' || field === 'rfc'
        ? value.toUpperCase()
        : value;

    setForm((prev) => ({ ...prev, [field]: newValue }));
    validateField(field, newValue);
  };

  const handleSubmit = async () => {
    const isValid = validateAll();
    if (!isValid) return;

    setIsUploading(true);
    let finalPath = form.fotoPerfil;

    try {
      // 1. Manejo de subida de imagen a S3
      if (selectedFile) {
        // Borrar imagen anterior de S3 si existía y era URL de S3
        if (previousImagePath && previousImagePath.startsWith('http')) {
          try {
            await axios.delete(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(previousImagePath)}`);
          } catch (e) {
            console.warn("No se pudo eliminar la imagen previa de S3", e);
          }
        }

        // Subir nueva imagen
        const formData = new FormData();
        formData.append('file', selectedFile);

        const resUpload = await axios.post(UPLOAD_ENDPOINT, formData);
        finalPath = resUpload.data.filePath;
      }
      // Si no hay archivo seleccionado pero se borró la foto (fotoPerfil vacía) y había una antes
      else if (!form.fotoPerfil && previousImagePath) {
        if (previousImagePath.startsWith('http')) {
          try {
            await axios.delete(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(previousImagePath)}`);
          } catch (e) {
            console.warn("No se pudo eliminar la imagen de S3", e);
          }
        }
        finalPath = "";
      }

      // 2. Enviar datos al componente padre
      const finalForm = { ...form, fotoPerfil: finalPath };
      delete finalForm.fotoPerfilPreview; // No enviar el blob URL local

      onSubmit(finalForm, mode);
    } catch (error) {
      console.error("Error en el proceso de guardado (Persona):", error);
      setErrors(prev => ({ ...prev, fotoPerfil: "Error al procesar la imagen." }));
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const curp = data.curp ?? '';
      const rfc = data.rfc ?? '';
      const fechaNacimientoRaw = data.fechaNacimiento ?? '';
      const fechaNacimiento =
        typeof fechaNacimientoRaw === 'string' && fechaNacimientoRaw.includes('T')
          ? fechaNacimientoRaw.split('T')[0]
          : fechaNacimientoRaw;

      const filledForm = {
        nombre: data.nombre ?? '',
        apellidoPaterno: data.apellidoPaterno ?? '',
        apellidoMaterno: data.apellidoMaterno ?? '',
        curp,
        rfc,
        genero: data.genero ?? '',
        fechaNacimiento,
        fotoPerfil: data.fotoPerfil ?? '',
      };

      setForm(filledForm);
      setPreviousImagePath(filledForm.fotoPerfil);

      // Validamos todo lo precargado
      const newErrors = {};
      for (const key of Object.keys(filledForm)) {
        validateField(key, filledForm[key]);
      }
    } else {
      setForm(EMPTY);
      setErrors({
        nombre: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        curp: '',
        rfc: '',
        genero: '',
        fechaNacimiento: '',
        fotoPerfil: '',
      });
      setPreviousImagePath(null);
      setSelectedFile(null);
    }
  }, [mode, data]);

  if (!open) return null;

  const allRequiredFilled =
    form.nombre.trim() &&
    form.apellidoPaterno.trim() &&
    form.genero &&
    form.fechaNacimiento;

  const hasErrors = Object.values(errors).some((e) => !!e);

  const isSaveDisabled = !allRequiredFilled || hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva persona' : 'Editar persona'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">
        ✕
      </button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      {/* Nombre */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nombre:</label>
        <br /><br />
        <input
          className={`infoField${errors.nombre ? ' infoField--error' : ''}`}
          value={form.nombre}
          onChange={(e) => handleChange('nombre', e.target.value)}
          maxLength={50}
        />
        {errors.nombre && <small style={errorStyle}>{errors.nombre}</small>}
      </div>

      {/* Apellido paterno */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Apellido paterno:</label>
        <br /><br />
        <input
          className={`infoField${errors.apellidoPaterno ? ' infoField--error' : ''}`}
          value={form.apellidoPaterno}
          onChange={(e) => handleChange('apellidoPaterno', e.target.value)}
          maxLength={70}
        />
        {errors.apellidoPaterno && <small style={errorStyle}>{errors.apellidoPaterno}</small>}
      </div>

      {/* Apellido materno */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Apellido materno (opcional):</label>
        <br /><br />
        <input
          className={`infoField${errors.apellidoMaterno ? ' infoField--error' : ''}`}
          value={form.apellidoMaterno}
          onChange={(e) => handleChange('apellidoMaterno', e.target.value)}
          maxLength={70}
        />
        {errors.apellidoMaterno && <small style={errorStyle}>{errors.apellidoMaterno}</small>}
      </div>

      {/* Fecha de nacimiento */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Fecha de nacimiento:</label>
        <br /><br />
        <input
          className={`infoField${errors.fechaNacimiento ? ' infoField--error' : ''}`}
          type="date"
          onKeyDown={(e) => e.preventDefault()}
          value={form.fechaNacimiento}
          onChange={(e) => handleChange('fechaNacimiento', e.target.value)}
        />
        {errors.fechaNacimiento && <small style={errorStyle}>{errors.fechaNacimiento}</small>}
      </div>

      {/* CURP */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>CURP (opcional):</label>
        <br /><br />
        <input
          className={`infoField${errors.curp ? ' infoField--error' : ''}`}
          value={form.curp}
          onChange={(e) => handleChange('curp', e.target.value)}
          maxLength={18}
        />
        {errors.curp && <small style={errorStyle}>{errors.curp}</small>}
      </div>

      {/* RFC */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>RFC (opcional):</label>
        <br /><br />
        <input
          className={`infoField${errors.rfc ? ' infoField--error' : ''}`}
          value={form.rfc}
          onChange={(e) => handleChange('rfc', e.target.value)}
          maxLength={13}
        />
        {errors.rfc && <small style={errorStyle}>{errors.rfc}</small>}
      </div>

      {/* Género */}
      <div style={{ gridColumn: '1 / 3' }}>
        <span className="roles-title" style={{ fontSize: '.95rem' }}>Género:</span>
        <br /><br />
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
            <input
              type="radio"
              name="genero"
              value="H"
              checked={form.genero === 'H'}
              onChange={(e) => handleChange('genero', e.target.value)}
            />
            <span>Hombre</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
            <input
              type="radio"
              name="genero"
              value="M"
              checked={form.genero === 'M'}
              onChange={(e) => handleChange('genero', e.target.value)}
            />
            <span>Mujer</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
            <input
              type="radio"
              name="genero"
              value="O"
              checked={form.genero === 'O'}
              onChange={(e) => handleChange('genero', e.target.value)}
            />
            <span>Otro</span>
          </label>
        </div>
        {errors.genero && <small style={errorStyle}>{errors.genero}</small>}
      </div>

      {/* Foto de Perfil */}
      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Foto de Perfil:</label>
        <br /><br />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          <div
            className={`file-uploader${(form.fotoPerfilPreview || form.fotoPerfil) ? ' file-uploader--filled' : ''}${errors.fotoPerfil ? ' file-uploader--error' : ''}${isUploading ? ' file-uploader--uploading' : ''}`}
          >
            {(form.fotoPerfilPreview || form.fotoPerfil) && (
              <div className="file-uploader__badge">
                <span>✓</span>
                <span>{selectedFile ? 'Archivo seleccionado' : 'Imagen cargada'}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    setForm(p => ({ ...p, fotoPerfil: '', fotoPerfilPreview: null }));
                    setSelectedFile(null);
                  }}
                  className="file-uploader__badge-close"
                >×</button>
              </div>
            )}

            {form.fotoPerfilPreview || form.fotoPerfil ? (
              <div className="file-uploader__preview">
                <img
                  src={form.fotoPerfilPreview || getImageUrl(form.fotoPerfil)}
                  alt="Vista previa"
                />
              </div>
            ) : null}

            <label
              className="file-uploader__label"
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                onClick={(e) => { e.target.value = ''; }}
                style={{ display: 'none' }}
                disabled={isUploading}
              />
              <div className="file-uploader__icon">
                {(form.fotoPerfilPreview || form.fotoPerfil) ? '🔄' : '📷'}
              </div>
              <div className="file-uploader__text">
                {(form.fotoPerfilPreview || form.fotoPerfil) ? 'Cambiar foto' : 'Subir foto de perfil'}
              </div>
              <div className="file-uploader__hint">
                JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)
              </div>
            </label>
          </div>
          {errors.fotoPerfil && <small style={errorStyle}>{errors.fotoPerfil}</small>}
        </div>
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={handleSubmit}
        disabled={isSaveDisabled || isUploading}
        style={{
          opacity: (isSaveDisabled || isUploading) ? 0.5 : 1,
          cursor: (isSaveDisabled || isUploading) ? 'not-allowed' : 'pointer',
        }}
      >
        {isUploading ? 'Procesando...' : (mode === 'new' ? 'Guardar' : 'Actualizar')}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
